/**
 * Capability Helpers - Backwards Compatibility & Utilities
 */
import { getCapabilityDefinition, STANDARD_CAPABILITIES } from './capabilityDefinitions';
/**
 * Check if capabilities are in legacy format (plain object with known fields)
 */
export function isLegacyFormat(capabilities) {
    if (!capabilities || typeof capabilities !== 'object') {
        return false;
    }
    // Check if it has old-style boolean/number values directly
    const hasDirectValues = typeof capabilities.supports5G === 'boolean' ||
        typeof capabilities.supports4G === 'boolean' ||
        typeof capabilities.maxDownloadSpeed === 'number';
    // Check if it's NOT new format (with type/value structure)
    const isNotNewFormat = !capabilities.supports5G?.type;
    return hasDirectValues && isNotNewFormat;
}
/**
 * Check if capabilities are in new dynamic format
 */
export function isDynamicFormat(capabilities) {
    if (!capabilities || typeof capabilities !== 'object') {
        return false;
    }
    // Check if values have type/value structure
    const firstKey = Object.keys(capabilities)[0];
    if (!firstKey)
        return false;
    const firstValue = capabilities[firstKey];
    return firstValue && typeof firstValue === 'object' && 'type' in firstValue && 'value' in firstValue;
}
/**
 * Convert legacy capabilities to dynamic format
 */
export function convertLegacyCapabilities(legacy) {
    return {
        'supports5G': { type: 'boolean', value: legacy.supports5G },
        'supports4G': { type: 'boolean', value: legacy.supports4G },
        'supports3G': { type: 'boolean', value: legacy.supports3G },
        'supports2G': { type: 'boolean', value: legacy.supports2G },
        'supportsVoLTE': { type: 'boolean', value: legacy.supportsVoLTE },
        'supportseSIM': { type: 'boolean', value: legacy.supportseSIM },
        'maxDownloadSpeed': { type: 'number', value: legacy.maxDownloadSpeed, unit: 'Mbps' },
        'maxUploadSpeed': { type: 'number', value: legacy.maxUploadSpeed, unit: 'Mbps' }
    };
}
/**
 * Convert dynamic capabilities to legacy format (for backwards compatibility)
 */
export function convertToLegacyCapabilities(dynamic) {
    return {
        supports5G: getCapabilityBooleanValue(dynamic, 'supports5G', false),
        supports4G: getCapabilityBooleanValue(dynamic, 'supports4G', false),
        supports3G: getCapabilityBooleanValue(dynamic, 'supports3G', false),
        supports2G: getCapabilityBooleanValue(dynamic, 'supports2G', false),
        supportsVoLTE: getCapabilityBooleanValue(dynamic, 'supportsVoLTE', false),
        supportseSIM: getCapabilityBooleanValue(dynamic, 'supportseSIM', false),
        maxDownloadSpeed: getCapabilityNumberValue(dynamic, 'maxDownloadSpeed', 0),
        maxUploadSpeed: getCapabilityNumberValue(dynamic, 'maxUploadSpeed', 0)
    };
}
/**
 * Normalize capabilities to dynamic format (auto-detects and converts if needed)
 */
export function normalizeCapabilities(capabilities) {
    if (!capabilities) {
        return {};
    }
    // Empty object is valid - return as-is without warning
    if (typeof capabilities === 'object' && Object.keys(capabilities).length === 0) {
        return {};
    }
    if (isLegacyFormat(capabilities)) {
        return convertLegacyCapabilities(capabilities);
    }
    if (isDynamicFormat(capabilities)) {
        return capabilities;
    }
    // Unknown format, return empty
    console.warn('Unknown capabilities format, returning empty:', capabilities);
    return {};
}
/**
 * Get capability value (returns actual value, not wrapped object)
 */
export function getCapabilityValue(capabilities, key, defaultValue) {
    const cap = capabilities[key];
    if (!cap)
        return defaultValue;
    return cap.value ?? defaultValue;
}
/**
 * Get boolean capability value
 */
export function getCapabilityBooleanValue(capabilities, key, defaultValue = false) {
    const value = getCapabilityValue(capabilities, key, defaultValue);
    return Boolean(value);
}
/**
 * Get number capability value
 */
export function getCapabilityNumberValue(capabilities, key, defaultValue = 0) {
    const value = getCapabilityValue(capabilities, key, defaultValue);
    return Number(value) || defaultValue;
}
/**
 * Get string capability value
 */
export function getCapabilityStringValue(capabilities, key, defaultValue = '') {
    const value = getCapabilityValue(capabilities, key, defaultValue);
    return String(value) || defaultValue;
}
/**
 * Get array capability value (list of strings, e.g., supported bands)
 */
export function getCapabilityArrayValue(capabilities, key, defaultValue = []) {
    const value = getCapabilityValue(capabilities, key, defaultValue);
    if (Array.isArray(value))
        return value.map(String);
    return defaultValue;
}
/**
 * Set capability value
 */
export function setCapabilityValue(capabilities, key, value, type, unit) {
    // Auto-detect type if not provided
    if (!type) {
        if (Array.isArray(value))
            type = 'array';
        else if (typeof value === 'boolean')
            type = 'boolean';
        else if (typeof value === 'number')
            type = 'number';
        else if (typeof value === 'string')
            type = 'string';
        else
            type = 'string';
    }
    const capValue = { type, value };
    if (unit)
        capValue.unit = unit;
    return {
        ...capabilities,
        [key]: capValue
    };
}
/**
 * Validate capabilities against standard definitions
 */
export function validateCapabilities(capabilities) {
    const errors = [];
    const warnings = [];
    for (const [key, value] of Object.entries(capabilities)) {
        const definition = getCapabilityDefinition(key);
        // Warn if not a standard capability
        if (!definition) {
            warnings.push(`"${key}" is not a standard capability (custom capability)`);
            continue;
        }
        // Validate type matches definition
        if (value.type !== definition.type) {
            errors.push(`"${key}" has type "${value.type}" but should be "${definition.type}"`);
        }
        // Validate enum options
        if (definition.type === 'enum' && definition.options) {
            if (!definition.options.includes(String(value.value))) {
                errors.push(`"${key}" has invalid value "${value.value}". Must be one of: ${definition.options.join(', ')}`);
            }
        }
        // Validate number ranges
        if (definition.type === 'number') {
            const numValue = Number(value.value);
            if (isNaN(numValue)) {
                errors.push(`"${key}" has non-numeric value: ${value.value}`);
            }
            if (numValue < 0) {
                warnings.push(`"${key}" has negative value: ${numValue}`);
            }
        }
        // Validate array values
        if (definition.type === 'array') {
            if (!Array.isArray(value.value)) {
                errors.push(`"${key}" must be an array, got ${typeof value.value}`);
            }
            else {
                const invalidItems = value.value.filter(v => typeof v !== 'string');
                if (invalidItems.length > 0) {
                    errors.push(`"${key}" array must contain only strings`);
                }
                // If options are defined, every element must be one of them
                if (definition.options && definition.options.length > 0) {
                    const allowed = new Set(definition.options);
                    const outOfSet = value.value.filter(v => !allowed.has(String(v)));
                    if (outOfSet.length > 0) {
                        errors.push(`"${key}" contains values not in allowed set: ${outOfSet.join(', ')}`);
                    }
                }
            }
        }
    }
    // Check required capabilities
    const requiredCaps = STANDARD_CAPABILITIES.filter(c => c.required);
    for (const required of requiredCaps) {
        if (!(required.id in capabilities)) {
            errors.push(`Missing required capability: "${required.id}"`);
        }
    }
    return {
        valid: errors.length === 0,
        errors,
        warnings
    };
}
/**
 * Create empty capabilities with default values
 */
export function createDefaultCapabilities() {
    const capabilities = {};
    for (const definition of STANDARD_CAPABILITIES) {
        if (definition.defaultValue !== undefined) {
            capabilities[definition.id] = {
                type: definition.type,
                value: definition.defaultValue,
                ...(definition.unit && { unit: definition.unit }),
                ...(definition.options && { options: definition.options })
            };
        }
    }
    return capabilities;
}
/**
 * Merge capabilities (second overrides first)
 */
export function mergeCapabilities(base, override) {
    return { ...base, ...override };
}
/**
 * Compute effective capabilities by merging ModemModel defaults with modem overrides.
 * Returns the final set of capabilities that should be used for scheduling/matching.
 */
export function computeEffectiveCapabilities(modemModelCapabilities, modemOverrides) {
    const base = normalizeCapabilities(modemModelCapabilities || {});
    const overrides = normalizeCapabilities(modemOverrides || {});
    return mergeCapabilities(base, overrides);
}
/**
 * Get capability source information for display purposes.
 * Returns which capabilities are inherited from ModemModel vs custom overrides.
 */
export function getCapabilitySources(modemModelCapabilities, modemOverrides) {
    const base = normalizeCapabilities(modemModelCapabilities || {});
    const overrides = normalizeCapabilities(modemOverrides || {});
    const baseKeys = new Set(Object.keys(base));
    const overrideKeys = new Set(Object.keys(overrides));
    const inherited = [];
    const overridden = [];
    const custom = [];
    // Find inherited (in base but not in overrides)
    for (const key of baseKeys) {
        if (!overrideKeys.has(key)) {
            inherited.push(key);
        }
        else {
            overridden.push(key);
        }
    }
    // Find custom (in overrides but not in base)
    for (const key of overrideKeys) {
        if (!baseKeys.has(key)) {
            custom.push(key);
        }
    }
    return { inherited, overridden, custom };
}
/**
 * Check if modem has any custom capability overrides
 */
export function hasAnyOverrides(modemModelCapabilities, modemOverrides) {
    const sources = getCapabilitySources(modemModelCapabilities, modemOverrides);
    return sources.overridden.length > 0 || sources.custom.length > 0;
}
/**
 * Check if modem matches required capabilities.
 *
 * Requirement shapes:
 *   boolean         → modem value must equal
 *   number          → modem value must be >= required (simple gte)
 *   string          → modem value must equal (enum/string)
 *   string[]        → each required value must be present in modem's array
 *                     (i.e., "modem covers all of these"). Pass
 *                     { values: [...], mode: 'any' } to require any overlap.
 */
export function matchesRequirements(modemCapabilities, requirements) {
    for (const [key, requiredValue] of Object.entries(requirements)) {
        const actualValue = getCapabilityValue(modemCapabilities, key);
        if (actualValue === undefined || actualValue === null) {
            return false;
        }
        // Array requirement
        if (Array.isArray(requiredValue)) {
            if (!Array.isArray(actualValue))
                return false;
            const actualSet = new Set(actualValue.map(String));
            for (const v of requiredValue) {
                if (!actualSet.has(String(v)))
                    return false;
            }
            continue;
        }
        // Object form allows { values: string[], mode: 'any' | 'all' }
        if (requiredValue && typeof requiredValue === 'object' && Array.isArray(requiredValue.values)) {
            if (!Array.isArray(actualValue))
                return false;
            const actualSet = new Set(actualValue.map(String));
            const mode = requiredValue.mode === 'any' ? 'any' : 'all';
            if (mode === 'any') {
                const overlap = requiredValue.values.some((v) => actualSet.has(String(v)));
                if (!overlap)
                    return false;
            }
            else {
                for (const v of requiredValue.values) {
                    if (!actualSet.has(String(v)))
                        return false;
                }
            }
            continue;
        }
        // Boolean: must match exactly
        if (typeof requiredValue === 'boolean') {
            if (actualValue !== requiredValue)
                return false;
        }
        // Number: actual must be >= required
        else if (typeof requiredValue === 'number') {
            if (Number(actualValue) < requiredValue)
                return false;
        }
        // String/Enum: must match exactly
        else if (typeof requiredValue === 'string') {
            if (String(actualValue) !== requiredValue)
                return false;
        }
    }
    return true;
}
// ============================================================================
// Resolving a catalog model from an edge-reported model string
// ============================================================================
/** Uppercase and strip non-alphanumerics so "EG25-G" ≡ "eg25 g" ≡ "EG25G". */
export function normalizeModelToken(value) {
    return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}
/**
 * Match an edge-reported model string against the `modem_models` catalog:
 * exact, then prefix in either direction (min 4 normalized chars — handles
 * "EG25" ↔ "EG25-G", "EM120R" ↔ "EM120R-GL"), then explicit `matchAliases`.
 *
 * Ambiguity returns null — wrong-but-confident is worse than unresolved, and
 * the caller renders "no data" rather than another module's specs.
 *
 * Manufacturer is deliberately NOT part of the match: the edge reports
 * `manufacturer: "Unknown"` for every modem in the fleet, so requiring it to
 * agree with the catalog's "Quectel" rejects every candidate.
 *
 * This mirrors `resolveModemModelMatch` in central-server, which does the
 * authoritative link onto `known_modems.modemModelId` at heartbeat. Use this
 * only as a DISPLAY fallback for the legacy `devices.modems[]` array, which
 * carries a model string but no link; prefer `modemModelId` wherever it is
 * present.
 */
export function resolveModemModelByReportedName(reportedModel, catalog) {
    const model = normalizeModelToken(reportedModel);
    if (!model)
        return null;
    const MIN_PREFIX_LEN = 4;
    const candidates = catalog.filter((entry) => {
        const entryModel = normalizeModelToken(entry.model);
        if (!entryModel)
            return false;
        if ((entry.matchAliases || []).some((a) => normalizeModelToken(a) === model))
            return true;
        if (entryModel === model)
            return true;
        if (entryModel.startsWith(model) && model.length >= MIN_PREFIX_LEN)
            return true;
        if (model.startsWith(entryModel) && entryModel.length >= MIN_PREFIX_LEN)
            return true;
        return false;
    });
    if (candidates.length === 1)
        return candidates[0];
    if (candidates.length > 1) {
        const exact = candidates.filter((c) => normalizeModelToken(c.model) === model);
        return exact.length === 1 ? exact[0] : null;
    }
    return null;
}
